import { AutocompleteProps, AutocompleteValue, FilterOptionsState, TextField } from '@mui/material';
import { GridCellParams, GridRenderEditCellParams, GridRowId, useGridApiContext } from '@mui/x-data-grid-premium';
import { SyntheticEvent, useEffect, useRef } from 'react';
import { stopPropagationUnlessCellExit } from 'src/utils/grid/cell/cellExit';

import Autocomplete, { ExtraAutoCompleteProps } from '../Autocomplete';

/**
 * TODO: preview the first match in the cell
 * Note: must be rendered as a column's `renderEditCell` — it reads
 * `useGridApiContext()`, so it only works inside a DataGrid. NOT tied to
 * `AddableDataGrid`: most call sites are `EditableDataGrid` surfaces
 * (bills, journal entries, pallet tags, portal line items, lot select).
 */
export default function AutoCompleteCell<
  T = { id?: number },
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  id,
  value,
  field,
  filterOptions,
  options,
  getOptionLabel,
  details,
  onChange,
  isOptionEqualToValue = areIdsEqual,
  textFieldProps,
  freeSolo,
  freeSoloPrefix,
  defaultValue = null,
  hasFocus,
  onClose,
  ...props
}: {
  id: GridRowId;
  value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
  field: string;
  filterOptions?: (options: T[], params: FilterOptionsState<T>) => T[];
  getOptionLabel: (option: T) => string;
  onChange?: (
    e: SyntheticEvent,
    newValue: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
    reason: string,
    details?: { option: T }
  ) => void;
  details?: (option, inputValue?: string) => React.ReactNode;
  freeSoloPrefix?: string;
  textFieldProps?: React.ComponentProps<typeof TextField>;
  options: T[];
  autoFocus?: boolean;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  freeSolo?: FreeSolo;
  defaultValue?: T;
  hasFocus?: boolean;
} & GridRenderEditCellParams &
  ExtraAutoCompleteProps &
  Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'id'> & {
    renderInput?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderInput'];
  }) {
  const apiRef = useGridApiContext();
  const ref = useRef<HTMLInputElement>();

  useEffect(() => {
    if (hasFocus) {
      ref.current?.focus({ preventScroll: true });
    }
  }, [hasFocus]);

  const handleChange =
    onChange ??
    (async (e, value) => {
      stopPropagationUnlessCellExit(e);
      await apiRef.current.setEditCellValue({ id, field, value });
    });

  // These are props that are being spread from `renderEditCell(params)` onto the autocomplete which throws a bunch of warnings
  // We should really move away from spread props and use a more explicit API for this, but this is the safest way to suppress the warnings for now
  const {
    rowNode: _,
    colDef: _a,
    cellMode: _b,
    isEditable: _c,
    isProcessingProps: _d,
    getPrediction: _e,
    formattedValue: _f,
    formatPrediction: _g,
    ...autocompleteProps
  } = props;

  return (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      value={value || defaultValue}
      freeSolo={freeSolo}
      fullWidth
      clearOnEscape={false}
      details={details}
      freeSoloPrefix={freeSoloPrefix}
      filterOptions={filterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      getOptionLabel={getOptionLabel}
      size="small"
      // A dropdown cell shows its options the moment it is ENTERED — by click or by
      // Tab. `Autocomplete` drives that off this `hasFocus`, which the grid flips
      // once per entry, so Escape can still close it for good.
      hasFocus={hasFocus}
      // Grid editors obey the One Law: every way of leaving a cell COMMITS what the user
      // chose. The Tab keydown this editor sees is only ONE of those ways — the grid's
      // own keymap swallows it whenever it has somewhere to navigate.
      commitPendingOnBlur
      border="none"
      onChange={handleChange}
      {...autocompleteProps}
      slotProps={{
        ...autocompleteProps.slotProps,
        popper: {
          ...autocompleteProps.slotProps?.popper,
          sx: { zIndex: 210001, minWidth: 'fit-content', maxWidth: '90vw', ...autocompleteProps.slotProps?.popper?.sx },
          modifiers: [{ name: 'offset', options: { offset: [0, 16] } }],
        } as NonNullable<typeof autocompleteProps.slotProps>['popper'],
        paper: {
          ...autocompleteProps.slotProps?.paper,
          sx: {
            borderRadius: '6px',
            boxShadow: '0 10px 16px -3px rgba(20, 21, 26, 0.08), 0 3px 6px -2px rgba(20, 21, 26, 0.05)',
            border: '1px solid rgba(10, 15, 41, 0.08)',
            width: 'calc(100% + 2px)',
            marginLeft: '-1px',
            ...autocompleteProps.slotProps?.paper?.sx,
          },
        },
        listbox: {
          ...autocompleteProps.slotProps?.listbox,
          sx: {
            '& .MuiAutocomplete-option': { fontSize: '14px', padding: '6px 8px', borderRadius: '4px', margin: '0 4px' },
            ...autocompleteProps.slotProps?.listbox?.sx,
          },
        },
      }}
      onClose={(e, reason) => {
        onClose?.(e, reason);
      }}
      textFieldProps={{
        inputRef: ref,
        fullWidth: true,
        ...textFieldProps,
        sx: {
          '& .MuiInputBase-input': { fontSize: '14px', outline: 'none' },
          '& .MuiInputBase-root': {
            overflow: 'hidden',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            wrap: 'nowrap',
            maxHeight: '64px',
            width: '100%',
            height: '100%',
            borderRadius: 0,
            gap: '4px',
          },
          ...textFieldProps?.sx,
        },
      }}
      sx={{ height: '100%', '& .MuiFormControl-root': { height: '100%' }, ...autocompleteProps.sx }}
    />
  );
}

const areIdsEqual = (option, value) => option.id === value.id;
export const autocompleteCellClassName = (params: GridCellParams) => {
  return `AutoCompleteCell-${params.cellMode}`;
};
